import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useJwt } from '../../context/JwtContext';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '../ui/Sidebar';
import { LogOut, Key } from 'lucide-react';

/**
 * Application sidebar component using shadcn/ui
 * @param {Object} props - Component props
 * @param {string} props.activeTab - The active tab in the dashboard
 * @param {Function} props.setActiveTab - Function to set the active tab
 * @returns {JSX.Element} AppSidebar component
 */
const AppSidebar = ({ activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { handleOpenJwtDialog } = useJwt();

  // Determine active section based on pathname and activeTab
  const isUsersPage = location.pathname === '/users' || activeTab === 'users';
  const isExternalPartnersPage = location.pathname === '/external-partners';
  const isCheckInPage =
    location.pathname === '/' ||
    location.pathname.includes('/check-in/') ||
    location.pathname.includes('/business4flyo/check-in/') ||
    location.pathname.includes('/ext-check-in/');

  const menuItems = [
    {
      key: 'webcheckinSla',
      label: 'Web Check-in SLA',
      isActive: isCheckInPage && activeTab === 'webcheckinSla',
      onClick: () => {
        setActiveTab('webcheckinSla');
        if (location.pathname !== '/') {
          navigate('/', { state: { activeTab: 'webcheckinSla' } });
        }
      }
    },
    {
      key: 'webCheckIn',
      label: 'WebCheckin (Core Flow)',
      isActive: isCheckInPage && activeTab === 'webCheckIn',
      onClick: () => {
        setActiveTab('webCheckIn');
        if (location.pathname !== '/') {
          navigate('/', { state: { activeTab: 'webCheckIn' } });
        }
      }
    },
    {
      key: 'b2bWebCheckIn',
      label: 'WebCheckin (Explera- Core Flow)',
      isActive: isCheckInPage && activeTab === 'b2bWebCheckIn',
      onClick: () => {
        setActiveTab('b2bWebCheckIn');
        if (location.pathname !== '/') {
          navigate('/', { state: { activeTab: 'b2bWebCheckIn' } });
        }
      }
    },
    {
      key: 'business4FlyoWebCheckIn',
      label: 'WebCheckin (F4B)',
      isActive: isCheckInPage && activeTab === 'business4FlyoWebCheckIn',
      onClick: () => {
        setActiveTab('business4FlyoWebCheckIn');
        if (location.pathname !== '/') {
          navigate('/', { state: { activeTab: 'business4FlyoWebCheckIn' } });
        }
      }
    },
    {
      key: 'extPartnerCheckin',
      label: 'WebCheckin (ExtPartner)',
      isActive: isCheckInPage && activeTab === 'extPartnerCheckin',
      onClick: () => {
        setActiveTab('extPartnerCheckin');
        if (location.pathname !== '/') {
          navigate('/', { state: { activeTab: 'extPartnerCheckin' } });
        }
      }
    },
    {
      key: 'tripInfo',
      label: 'Trip Info (Core Flow)',
      isActive: isCheckInPage && activeTab === 'tripInfo',
      onClick: () => {
        setActiveTab('tripInfo');
        if (location.pathname !== '/') {
          navigate('/', { state: { activeTab: 'tripInfo' } });
        }
      }
    },
    {
      key: 'users',
      label: 'Users (Core Flow)',
      isActive: isUsersPage,
      onClick: () => {
        navigate('/users');
      }
    },
    {
      key: 'agents',
      label: 'Agents (F4B)',
      isActive: location.pathname === '/agents' || location.pathname.startsWith('/agents/'),
      onClick: () => {
        navigate('/agents');
      }
    },
    {
      key: 'externalPartners',
      label: 'External Partners (extPartner)',
      isActive: isExternalPartnersPage,
      onClick: () => navigate('/external-partners'),
    },
    // Web Check-in Dashboard - TEMPORARILY COMMENTED OUT
    // {
    //   key: 'webCheckinDashboard',
    //   label: 'Web Check-in Dashboard',
    //   isActive: isCheckInPage && activeTab === 'webCheckinDashboard',
    //   onClick: () => {
    //     setActiveTab('webCheckinDashboard');
    //     if (isCheckInDetailsPage || isUsersPage || isBookingsPage) {
    //       navigate('/', { state: { activeTab: 'webCheckinDashboard' } });
    //     }
    //   }
    // },
    // Bookings - TEMPORARILY COMMENTED OUT
    // {
    //   key: 'bookings',
    //   label: 'Bookings',
    //   isActive: isBookingsPage,
    //   onClick: () => {
    //     navigate('/bookings');
    //   }
    // },
  ];

  return (
    <Sidebar className="border-r w-64 shrink-0" collapsible="none">
      <SidebarHeader className="p-4">
        <h1 className="text-sm font-bold text-sidebar-foreground">flyo.ai</h1>
      </SidebarHeader>
      
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.key}>
                  <SidebarMenuButton
                    onClick={item.onClick}
                    isActive={item.isActive}
                    className="w-full justify-start text-xs"
                  >
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4">
        <SidebarSeparator className="mb-4" />
        
        {/* User Info */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
            {user && user.name ? user.name.charAt(0) : 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-sidebar-foreground truncate">
              {user ? user.name || 'User' : 'User'}
            </p>
            <p className="text-xs text-sidebar-foreground/70 truncate">
              {user ? user.email : ''}
            </p>
          </div>
        </div>

        {/* JWT Button */}
        <SidebarMenuButton
          onClick={handleOpenJwtDialog}
          className="w-full justify-center text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 mb-2"
        >
          <Key className="h-4 w-4" />
          <span>Generate JWT</span>
        </SidebarMenuButton>

        {/* Logout Button */}
        <SidebarMenuButton
          onClick={logout}
          className="w-full justify-center text-xs bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700"
        >
          <LogOut className="h-4 w-4" />
          <span>Logout</span>
        </SidebarMenuButton>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
